import {useMemo, useState} from 'react';
import {CogIcon} from '@heroicons/react/outline';
import {Spinner} from '@tyrannosaurustech/ui';

import {ContentWrapper} from '../../../components/ContentWrapper';
import {TitleM, ParagraphS} from '../../../components/atoms/Typography';
import {InitialsAvatar} from '../../../components/atoms/Avatar';
import {Theme} from '../../../components/atoms/theme';
import {useGetVettingStatsQuery} from '../../../redux/reduxQuery/vettingApi';
import {ChartStyles, DeltaPill, Legend} from '../TeamActivity/charts';
import {fmtDateTime} from '../TeamActivity/constants';
import {
  DEFAULT_DATE_RANGE,
  DateRangeSelection,
  VettingStats,
  buildUserSeries,
  computeDateRangeWindow,
  fmtAge,
  formatPriorLabel,
  formatRangeLabel,
  statusLabel,
} from './constants';
import {CombinedStackedChart, MetricLegend} from './charts';
import {DateRangePicker} from './DateRangePicker';
import {ConfigModal} from './ConfigModal';

const initialsOf = (name: string) =>
  (name || '?')
    .split(' ')
    .map((s) => s.charAt(0))
    .slice(0, 2)
    .join('');

export default function Vetting() {
  const [range, setRange] = useState<DateRangeSelection>(DEFAULT_DATE_RANGE);
  const [configOpen, setConfigOpen] = useState(false);

  const rangeLabel = formatRangeLabel(range);
  const priorLabel = formatPriorLabel(range);
  const win = useMemo(() => computeDateRangeWindow(range), [range]);

  const {data, isLoading} = useGetVettingStatsQuery({
    startDate: win.startDate,
    endDate: win.endDate,
  });
  const {data: prevData} = useGetVettingStatsQuery({
    startDate: win.prevStartDate,
    endDate: win.prevEndDate,
  });

  const stats: VettingStats | undefined = data;
  const series = useMemo(() => buildUserSeries(stats, win), [stats, win]);

  // Prior-window totals, restricted to the same people the page shows so
  // "vs prior" compares like with like
  const prevTotals = useMemo(() => {
    const trackedIds = new Set((stats?.trackedUsers || []).map((u: any) => u.id));
    const caseMap: Record<string, number> = {};
    const providerMap: Record<string, number> = {};
    (prevData?.caseCompletions || []).forEach((row: any) => {
      if (trackedIds.size && !trackedIds.has(row.userId)) return;
      caseMap[row.userId] = (caseMap[row.userId] || 0) + row.count;
    });
    (prevData?.providerCompletions || []).forEach((row: any) => {
      if (trackedIds.size && !trackedIds.has(row.userId)) return;
      providerMap[row.userId] = (providerMap[row.userId] || 0) + row.count;
    });
    return {caseMap, providerMap};
  }, [prevData, stats]);

  const totalCasesCompleted = series.reduce((a, s) => a + s.caseTotal, 0);
  const totalProvidersVerified = series.reduce((a, s) => a + s.providerTotal, 0);
  const prevCaseTotal = Object.values(prevTotals.caseMap).reduce((a, v) => a + v, 0);
  const prevProviderTotal = Object.values(prevTotals.providerMap).reduce((a, v) => a + v, 0);
  const topVetter = [...series].sort((a, b) => b.total - a.total)[0];

  const pending = stats?.pending || [];
  const pendingTotal = pending.reduce((a, row) => a + row.count, 0);
  const oldestPending = pending.reduce<string | null>(
    (oldest, row) => (row.oldest && (!oldest || new Date(row.oldest) < new Date(oldest)) ? row.oldest : oldest),
    null,
  );
  const maxPendingCount = Math.max(...pending.map((row) => row.count), 1);

  const chartSeries = series.map((s) => ({
    key: s.userId,
    label: s.userName,
    color: s.color,
    caseValues: s.casePerBucket,
    providerValues: s.providerPerBucket,
  }));

  return (
    <ContentWrapper>
      <ChartStyles />
      <div className="flex flex-wrap items-end justify-between gap-4 pt-6 pb-4">
        <div>
          <TitleM style={{margin: 0}}>Vetting</TitleM>
          <ParagraphS style={{margin: '4px 0 0', color: '#6C6579'}}>
            Line items moved through vetting and providers verified by the team, plus what is still outstanding.
          </ParagraphS>
        </div>
        <div className="flex items-center gap-2">
          <DateRangePicker value={range} onChange={setRange} />
          <button
            onClick={() => setConfigOpen(true)}
            className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
          >
            <CogIcon width={14} />
            Settings
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-24">
          <Spinner colorClassName="text-primary" />
        </div>
      ) : (
        <>
          {stats && !stats.config.trackedUserIds.length && (
            <div className="ta-fadeup mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-purple-200 bg-purple-50 px-4 py-3">
              <div className="text-sm text-gray-700">
                <span className="font-bold">No vetting team configured.</span> Showing everyone with completions — pick your vetters in
                Settings to make this dashboard theirs.
              </div>
              <button
                onClick={() => setConfigOpen(true)}
                className="rounded-lg px-3 py-1.5 text-xs font-bold text-white"
                style={{background: Theme.colors.primaries_000}}
              >
                Configure
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 pb-5 lg:grid-cols-4">
            <div className="ta-card ta-fadeup rounded-xl border border-gray-200 bg-white p-4">
              <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400">Line items completed · {rangeLabel}</div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="text-2xl font-bold">{totalCasesCompleted}</span>
                <DeltaPill current={totalCasesCompleted} prev={prevCaseTotal} />
              </div>
              <div className="text-xs text-gray-500">vs. the prior {priorLabel}</div>
            </div>
            <div className="ta-card ta-fadeup rounded-xl border border-gray-200 bg-white p-4">
              <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400">Providers verified · {rangeLabel}</div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="text-2xl font-bold">{totalProvidersVerified}</span>
                <DeltaPill current={totalProvidersVerified} prev={prevProviderTotal} />
              </div>
              <div className="text-xs text-gray-500">vs. the prior {priorLabel}</div>
            </div>
            <div className="ta-card ta-fadeup rounded-xl border border-gray-200 bg-white p-4">
              <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400">Outstanding now</div>
              <div className="mt-1 text-2xl font-bold">{pendingTotal}</div>
              <div className="text-xs text-gray-500">{pendingTotal ? `Oldest waiting ${fmtAge(oldestPending)}` : 'nothing pending'}</div>
            </div>
            <div className="ta-card ta-fadeup rounded-xl border border-gray-200 bg-white p-4">
              <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400">Top vetter · {rangeLabel}</div>
              <div className="mt-1 truncate text-2xl font-bold">{topVetter && topVetter.total > 0 ? topVetter.userName : '—'}</div>
              <div className="text-xs text-gray-500">
                {topVetter && topVetter.total > 0 ? `${topVetter.total} completed` : 'no completions yet'}
              </div>
            </div>
          </div>

          <div className="mb-5 rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-3 pb-2">
              <div className="text-sm font-bold">Completed per bucket</div>
              <div className="flex items-center gap-4">
                <MetricLegend />
                {chartSeries.length > 1 && (
                  <Legend
                    items={chartSeries.map((s) => ({
                      key: s.key,
                      label: s.label,
                      color: s.color,
                    }))}
                  />
                )}
              </div>
            </div>
            <CombinedStackedChart bucketLabels={win.bucketLabels} series={chartSeries} />
          </div>

          <div className="grid gap-5 lg:grid-cols-3">
            <div className="rounded-xl border border-gray-200 bg-white lg:col-span-2">
              <div className="px-4 pt-4 pb-2 text-sm font-bold">By team member</div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-[10.5px] font-bold uppercase tracking-wider text-gray-400">
                      <th className="px-4 py-2">Member</th>
                      <th className="px-4 py-2 text-right">Line items</th>
                      <th className="px-4 py-2 text-right">Providers</th>
                      <th className="px-4 py-2 text-right">Total</th>
                      <th className="px-4 py-2">Last activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {series.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-400">
                          No completions in this window.
                        </td>
                      </tr>
                    )}
                    {series.map((s) => (
                      <tr key={s.userId} className="ta-row border-b border-gray-100 last:border-0">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <InitialsAvatar text={initialsOf(s.userName)} background={s.color} hover={s.color} size={36} />
                            <span className="text-sm font-semibold">{s.userName || 'Unknown user'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-semibold tabular-nums">{s.caseTotal}</td>
                        <td className="px-4 py-3 text-right text-sm font-semibold tabular-nums">{s.providerTotal}</td>
                        <td className="px-4 py-3 text-right text-base font-bold tabular-nums">{s.total}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-500">{s.lastAt ? fmtDateTime(s.lastAt) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="pb-1 text-sm font-bold">Outstanding by status</div>
              <div className="pb-3 text-xs text-gray-500">Line items currently awaiting vetting work.</div>
              {pending.length === 0 && (
                <div className="py-8 text-center text-sm text-gray-400">Nothing outstanding — the queue is clear.</div>
              )}
              {pending.map((row) => (
                <div key={row.status} className="mb-3 last:mb-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-sm font-medium">{statusLabel(row.status)}</span>
                    <span className="text-sm font-bold tabular-nums">{row.count}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <div
                      style={{
                        flex: 1,
                        height: 8,
                        borderRadius: 4,
                        background: '#F1EDF6',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        className="ta-mixseg"
                        style={{
                          width: `${(row.count / maxPendingCount) * 100}%`,
                          height: '100%',
                          borderRadius: 4,
                          background: Theme.colors.primaries_000,
                        }}
                      />
                    </div>
                    <span className="w-14 text-right font-mono text-[11px] text-gray-400">
                      {row.oldest ? `${fmtAge(row.oldest)} old` : ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <ParagraphS style={{color: '#9A93A8', margin: '14px 2px 32px'}}>
            Line item completions come from the audit log (status moves into a completed status); provider completions come from provider
            verifications. Team and criteria are configurable in Settings.
          </ParagraphS>
        </>
      )}

      <ConfigModal isOpen={configOpen} onClose={() => setConfigOpen(false)} />
    </ContentWrapper>
  );
}
